<template>
  <div>
    <TabsNavigation v-if="categoryTabs?.length > 0" :tabs="categoryTabs" class="mb-6" />

    <div v-if="selectedCategoryName" class="mt-6">
      <RetributionBoosterCard v-model="selectedTier" />
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <RetributionCard
          v-for="group in getRetributionConfigurationsWithSeparators(selectedCategoryName)"
          :key="`priceband-${group.priceBandMin}-${group.priceBandMax}`"
          :retributionConfigurations="group.retributionConfigurations"
          :priceBandMin="group.priceBandMin"
          :priceBandMax="group.priceBandMax"
          :multiplicator="selectedMultiplicator"
          :showManagerRetributionConfiguration="showManagerRetributionConfiguration"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { formatPrice, formatPriceBand, TabsNavigation } from "@estaly/ui";
import RetributionCard from "./RetributionCard.vue";
import RetributionBoosterCard from "./RetributionBoosterCard.vue";
import { boosterThresholds } from "@/utils/booster";

export default {
  components: {
    TabsNavigation,
    RetributionCard,
    RetributionBoosterCard,
  },

  props: {
    retributionConfigurations: {
      type: Array,
      required: true,
    },
    showManagerRetributionConfiguration: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedTier: "none",
      boosterThresholds,
    };
  },

  watch: {
    "$route.query.tier": {
      handler(newTier) {
        const isValidTier = newTier && this.boosterThresholds.some((t) => t.tier === newTier);
        this.selectedTier = isValidTier ? newTier : "none";
      },
      immediate: true,
    },
    selectedTier: {
      handler(newTier) {
        this.$router.replace({
          query: { ...this.$route.query, tier: newTier === "none" ? undefined : newTier },
        });
      },
    },
    categoryNames: {
      handler(newCategoryNames) {
        const currentCategory = this.$route.query.category;
        const hasCategories = newCategoryNames.length > 0;
        const firstCategory = newCategoryNames[0];
        const shouldUpdateCategory = hasCategories && (!currentCategory || !newCategoryNames.includes(currentCategory));
        if (shouldUpdateCategory) {
          this.$router.replace({
            query: { ...this.$route.query, category: firstCategory },
          });
        }
      },
      immediate: true,
    },
  },

  computed: {
    categoryNames() {
      return [...new Set(this.retributionConfigurations.map((row) => row.categoryName))];
    },

    categoryTabs() {
      return this.categoryNames.map((categoryName) => ({
        name: categoryName,
        displayName: categoryName,
        query: { category: categoryName },
      }));
    },

    selectedCategoryName() {
      const { category } = this.$route.query;
      return this.categoryNames.includes(category) ? category : (this.categoryNames[0] ?? null);
    },

    selectedMultiplicator() {
      const sortedThresholds = [...this.boosterThresholds].sort((a, b) => a.threshold - b.threshold);
      const tiersList = [
        { key: "none", multiplicator: 0 },
        ...sortedThresholds.map((t) => ({ key: t.tier, multiplicator: t.multiplicator })),
      ];
      const tier = tiersList.find((t) => t.key === this.selectedTier);
      return tier?.multiplicator ?? 0;
    },
  },

  methods: {
    formatPrice,
    formatPriceBand,

    orderRetributionConfiguration(a, b) {
      // Sort by type (monthly first)
      if (a.monthlyBilling && !b.monthlyBilling) return -1;
      if (!a.monthlyBilling && b.monthlyBilling) return 1;
      if (a.monthlyBilling && b.monthlyBilling) {
        return parseFloat(a.priceBandMin) - parseFloat(b.priceBandMin);
      }
      // Both are not monthly
      if (a.termLength !== b.termLength) {
        return a.termLength - b.termLength;
      }
      // Same termLength, sort by priceBandMin
      return parseFloat(a.priceBandMin) - parseFloat(b.priceBandMin);
    },

    getRetributionConfigurationsByCategory(categoryName) {
      return this.retributionConfigurations
        .filter((r) => r.categoryName === categoryName)
        .sort(this.orderRetributionConfiguration);
    },

    getRetributionConfigurationsWithSeparators(categoryName) {
      const retributionConfigurations = this.getRetributionConfigurationsByCategory(categoryName);
      // Group by price band
      const groupedByPriceBand = retributionConfigurations.reduce((result, retributionConfiguration) => {
        const key = `${retributionConfiguration.priceBandMin}-${retributionConfiguration.priceBandMax}`;
        if (!result[key]) {
          result[key] = {
            priceBandMin: retributionConfiguration.priceBandMin,
            priceBandMax: retributionConfiguration.priceBandMax,
            retributionConfigurations: [],
          };
        }
        result[key].retributionConfigurations.push(retributionConfiguration);
        return result;
      }, {});

      Object.values(groupedByPriceBand).forEach((group) => {
        group.retributionConfigurations.sort(this.orderRetributionConfiguration);
      });

      return Object.values(groupedByPriceBand).sort((a, b) => parseFloat(a.priceBandMin) - parseFloat(b.priceBandMin));
    },
  },
};
</script>
